import { z } from 'zod';
import { TableColumnSort } from '@applyboard/crystal-ui';
import { request, useQuery } from '@applyboard/ui-utils';
import { GIC_ACCEPTED_COUNTRY_OPTION_LIST, PROVINCE_OPTION_LIST } from '../common/constants';

const GicApplicationAttributesSchema = z.object({
  external_id: z.string().optional(),
  gic_id: z.string().nullable().optional(),
  student_name: z.string().optional(),
  email: z.string().optional(),
  created_date: z.string().optional(),
  destination_province: z.string().optional(),
  nationality: z.string().optional(),
  student_id: z.string().nullable().optional(),
  rp_id: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
});

const GicApplicationSchema = z.object({
  type: z.string(),
  id: z.string(),
  attributes: GicApplicationAttributesSchema,
});

const GicApplicationsResponseSchema = z.object({
  data: z.array(GicApplicationSchema),
  meta: z.object({
    total: z.number(),
  }),
});

export type GicApplicationAttributes = z.infer<typeof GicApplicationAttributesSchema>;
export type GicApplication = z.infer<typeof GicApplicationSchema>;
export type GicApplicationsResponse = z.infer<typeof GicApplicationsResponseSchema>;

export const GIC_APPLICATIONS_URL = '/api/gic/v2/applications';

export interface FetchGicApplicationsParams {
  page: number;
  pageSize: number;
  search?: string;
  sortOrder: TableColumnSort;
}

const sortMap: Record<string, string> = {
  created_date: 'created_at',
};

const fetchGicApplications = async (
  params: FetchGicApplicationsParams,
): Promise<GicApplicationsResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append('page[number]', params.page.toString());
  queryParams.append('page[size]', params.pageSize.toString());

  if (params.search) {
    queryParams.append('search', encodeURI(params.search));
  }

  // default sort by -created_at
  let sortParam = '-created_at';
  // only take the first element to sort
  if (params.sortOrder && params.sortOrder.length > 0 && sortMap[params.sortOrder[0].id]) {
    const direction = params.sortOrder[0].desc ? '-' : '';
    sortParam = `${direction}${sortMap[params.sortOrder[0].id]}`;
  }
  queryParams.append('sort', sortParam);

  const url = `${GIC_APPLICATIONS_URL}?${queryParams.toString()}`;

  const response = await request(url, {
    method: 'GET',
    schema: GicApplicationsResponseSchema,
    throwError: async (response: Response) => {
      const jsonMessage = await response.json();
      throw new Error(jsonMessage.message || 'Error fetching GIC applications');
    },
  });

  return response;
};

const mapProvinceAndCountry = (applications: GicApplicationAttributes[]) => {
  applications.map(application => {
    if (application.destination_province !== undefined) {
      application.destination_province =
        PROVINCE_OPTION_LIST[application.destination_province] != undefined
          ? PROVINCE_OPTION_LIST[application.destination_province]
          : application.destination_province;
    }

    if (application.nationality !== undefined) {
      application.nationality =
        GIC_ACCEPTED_COUNTRY_OPTION_LIST[application.nationality] != undefined
          ? GIC_ACCEPTED_COUNTRY_OPTION_LIST[application.nationality]
          : application.nationality;
    }
  });
  return applications;
};

const transformData = (data: GicApplicationsResponse) => {
  const transformedData = {
    applications: data.data.map(application => application.attributes ?? {}) || [],
    total: data.meta.total,
  };
  const transformedProvinces = mapProvinceAndCountry(transformedData.applications);
  transformedData.applications = transformedProvinces;
  return transformedData;
};

const useGicApplications = (params: FetchGicApplicationsParams) => {
  const { data, error, isLoading, isError } = useQuery(
    ['gicApplications', params],
    () => fetchGicApplications(params),
    {
      keepPreviousData: true,
      select: transformData,
    },
  );

  return {
    data: data?.applications,
    totalDataLength: data?.total,
    error,
    isLoading,
    isError,
  };
};

/**
 * Fetch single application by id
 */

const attributes = z
  .object({
    external_id: z.string().nullable().optional(),
    gic_id: z.string().nullable().optional(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
    school_province: z.string().nullable().optional(),
    student_nationality: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    student_date_of_birth: z.string().nullable().optional(),
    contact_phone_number: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    latest_status_date: z.string().nullable().optional(),
    recruitment_partner_id: z.string().nullable().optional(),
    student_id: z.string().nullable().optional(),
    school_name: z.string().nullable().optional(),
  })
  .optional();

export const FetchGicApplicationByIdResponseSchema = z.object({
  data: z.object({ id: z.string(), type: z.string(), attributes: attributes }),
});
export type GicApplicationResponse = z.infer<typeof FetchGicApplicationByIdResponseSchema>;

const fetchGicApplicationById = async (id: string): Promise<GicApplicationResponse> => {
  const url = `${GIC_APPLICATIONS_URL}/${id}`;

  const response = await request(url, {
    method: 'GET',
    schema: FetchGicApplicationByIdResponseSchema,
    throwError: async (response: Response) => {
      const jsonMessage = await response.json();
      throw new Error(jsonMessage.message || 'Error fetching GIC application by ID');
    },
  });

  return response;
};

const useGicApplication = (id: string) => {
  const { data, error, isLoading, isError } = useQuery(
    ['gicApplication', id],
    () => fetchGicApplicationById(id),
    {
      enabled: !!id, // Only run the query if an ID is provided
    },
  );

  return {
    data: data ? { ...data.data.attributes, id: data.data.id } : undefined,
    error,
    isLoading,
    isError,
  };
};

const loaAttributes = z.object({
  presigned_url: z.string(),
});

export const FetchGicLoaByIdResponseSchema = z.object({
  data: z.object({ id: z.string().optional(), type: z.string(), attributes: loaAttributes }),
});
export type GicLoaResponse = z.infer<typeof FetchGicLoaByIdResponseSchema>;

const fetchSupportingDocument = async (
  id: string,
  packageType = 'loa',
): Promise<GicLoaResponse> => {
  const url = `${GIC_APPLICATIONS_URL}/${id}/${packageType}`;
  const response = await request(url, {
    method: 'GET',
    schema: FetchGicLoaByIdResponseSchema,
    throwError: async (response: Response) => {
      const jsonMessage = await response.json();
      throw new Error(jsonMessage.message || 'Error fetching GIC LOA by ID');
    },
  });
  return response;
};

export { useGicApplications, useGicApplication, fetchSupportingDocument };
